import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { FeedbackType } from "components/common/FeedbackButtons"
import { ResumeQuestionHistoryItem } from "types/external/swayam/formativeAssessment"
import { getDifficultyString, PROBLEM_ASSESSMENT_TYPE_MAPPING } from "utils/external/swayam/formativeAssessment"

type AnswerStatusType = "correct" | "wrong" | "partial"

// Helper to map problem type string to stage mapping key
const getProblemTypeKeyForSlice = (problemType: string | number): number => {
  if (typeof problemType === "number") {
    return problemType
  }
  const typeMap: Record<string, number> = {
    mcq: 1,
    msq: 2,
    short_answer: 3,
    long_answer: 4,
    numeric: 5,
  }
  return typeMap[problemType.toLowerCase()] || 1
}

interface QuestionHistoryItem {
  testSessionDetails: any
  stage: string
  problemIndex: number
  attemptCount: number
  submitFormativeAssessmentData?: any
  selectedMCQOption?: any
  selectedMSQOptions?: any[]
  textAnswer?: string
  // View state for preserving feedback display
  isShowingWrongFeedback?: boolean
  persistedQuickHint?: string | null
  persistedLongHint?: string | null
  persistedAnswerStatusType?: AnswerStatusType | null
}

interface FormativeAssessmentInitialStates {
  stage: string
  currentTestSessionDetails: any
  selectedAssessmentMCQOption: any
  submitFormativeAssessmentData: any
  hasFormativeAssessmentEnded: boolean
  selectedAssessmentMSQOptions: any
  auditLogsHeartbeatStarted: boolean
  auditLogsCompleteData: any
  formativeAssessmentResult: any
  formativeAssessmentProblemsLeft: any
  formativeAssessmentTotalProblems: any
  questionHistory: QuestionHistoryItem[]
  currentQuestionIndex: number
  selectedDifficultyLevel: number
  startEventId: string | null
}

const initialState: FormativeAssessmentInitialStates = {
  stage: PROBLEM_ASSESSMENT_TYPE_MAPPING[0],
  currentTestSessionDetails: null,
  selectedAssessmentMCQOption: null,
  submitFormativeAssessmentData: null,
  formativeAssessmentProblemsLeft: null,
  formativeAssessmentTotalProblems: null,
  hasFormativeAssessmentEnded: false,
  selectedAssessmentMSQOptions: [],
  auditLogsHeartbeatStarted: false,
  formativeAssessmentResult: null,
  questionHistory: [],
  currentQuestionIndex: -1,
  selectedDifficultyLevel: -1,
  startEventId: null,
  auditLogsCompleteData: {
    video_url: "video_na",
    widget_code: "formative_assessment",
    action_performed: null,
    payload: {
      input_params_obj: {
        type: "qna",
        scope: "course",
        topic: "topic_na",
        difficulty_level: "all",
        question_type: "all",
        consider_history: false,
        custom_instructions: null,
      },
      assessment_obj: {
        topic: "topic_na",
        time_spent_seconds: 0,
        questions_attempted: 0,
        questions_total: 0,
        overall_ai_feedback_text: "overall_ai_feedback_text_na",
        ques_details: [],
        completion_status: "partial",
        areas_of_improvement: ["areas_of_improvement_na"],
        areas_of_strengths: ["areas_of_strengths_na"],
        rubric_score: ["rubric_score_na"],
        overall_score: 0,
      },
      blob_uri: {
        complete_blob_uri: [],
        generated_blob_uri: [],
        uploaded_blob_uri: [],
        downloaded_blob_uri: [],
      },
      compute_time: 0,
      tokens_used: 0,
      cache_hit: true,
      session_timeout: false,
      usage_time_in_sec: 0,
      watch_percentage: 0,
      video_duration: 0,
    },
  },
}

const FormativeAssessmentSlice = createSlice({
  name: "formative-assessment",
  initialState,
  reducers: {
    setStage: (state, action: PayloadAction<string>) => {
      state.stage = action.payload
    },
    setCurrentTestSessionDetails: (state, action: PayloadAction<any>) => {
      state.currentTestSessionDetails = action.payload
    },
    setSelectedAssessmentMCQOption: (state, action: PayloadAction<any>) => {
      state.selectedAssessmentMCQOption = action.payload
    },
    setSubmitFormativeAssessmentData: (state, action: PayloadAction<any>) => {
      state.submitFormativeAssessmentData = action.payload
    },
    setHasFormativeAssessmentEnded: (state, action: PayloadAction<any>) => {
      state.hasFormativeAssessmentEnded = action.payload
    },
    setSelectedAssessmentMSQOptions: (state, action: PayloadAction<any>) => {
      state.selectedAssessmentMSQOptions = action.payload
    },
    setAuditLogsHeartbeatStarted: (state, action: PayloadAction<boolean>) => {
      state.auditLogsHeartbeatStarted = action.payload
    },
    setAuditLogsCompleteData: (state, action: PayloadAction<any>) => {
      state.auditLogsCompleteData = action.payload
    },
    setFormativeAssessmentResult: (state, action: PayloadAction<any>) => {
      state.formativeAssessmentResult = action.payload
    },
    setFormativeAssessmentProblemsLeft: (state, action: PayloadAction<any>) => {
      state.formativeAssessmentProblemsLeft = action.payload
    },
    setFormativeAssessmentTotalProblems: (state, action: PayloadAction<any>) => {
      state.formativeAssessmentTotalProblems = action.payload
    },
    addToQuestionHistory: (
      state,
      action: PayloadAction<{ testSessionDetails: any; stage: string; problemIndex: number }>
    ) => {
      // Only add if this is a new question (not navigating back)
      const existingIndex = state.questionHistory.findIndex(
        (item) => item.testSessionDetails?.currentProblem?.id === action.payload.testSessionDetails?.currentProblem?.id
      )
      if (existingIndex === -1) {
        state.questionHistory.push({ ...action.payload, attemptCount: 0 })
        state.currentQuestionIndex = state.questionHistory.length - 1
      }
    },
    updateCurrentQuestionFeedback: (
      state,
      action: PayloadAction<{
        submitFormativeAssessmentData?: any
        selectedMCQOption?: any
        selectedMSQOptions?: any[]
        textAnswer?: string
        isShowingWrongFeedback?: boolean
        persistedQuickHint?: string | null
        persistedLongHint?: string | null
        persistedAnswerStatusType?: AnswerStatusType | null
      }>
    ) => {
      if (state.currentQuestionIndex >= 0 && state.currentQuestionIndex < state.questionHistory.length) {
        const currentQuestion = state.questionHistory[state.currentQuestionIndex]
        if (currentQuestion) {
          state.questionHistory[state.currentQuestionIndex] = {
            ...currentQuestion,
            ...action.payload,
          }
        }
      }
    },
    goToPreviousQuestion: (state) => {
      if (state.currentQuestionIndex > 0) {
        state.currentQuestionIndex -= 1
        const previousQuestion = state.questionHistory[state.currentQuestionIndex]
        if (previousQuestion) {
          state.currentTestSessionDetails = previousQuestion.testSessionDetails
          state.stage = previousQuestion.stage
          state.submitFormativeAssessmentData = previousQuestion.submitFormativeAssessmentData || null
          state.selectedAssessmentMCQOption = previousQuestion.selectedMCQOption || null
          state.selectedAssessmentMSQOptions = previousQuestion.selectedMSQOptions || []
        }
      }
    },
    goToNextQuestion: (state) => {
      if (state.currentQuestionIndex < state.questionHistory.length - 1) {
        state.currentQuestionIndex += 1
        const nextQuestion = state.questionHistory[state.currentQuestionIndex]
        if (nextQuestion) {
          state.currentTestSessionDetails = nextQuestion.testSessionDetails
          state.stage = nextQuestion.stage
          state.submitFormativeAssessmentData = nextQuestion.submitFormativeAssessmentData || null
          state.selectedAssessmentMCQOption = nextQuestion.selectedMCQOption || null
          state.selectedAssessmentMSQOptions = nextQuestion.selectedMSQOptions || []
        }
      }
    },
    setCurrentQuestionIndex: (state, action: PayloadAction<number>) => {
      state.currentQuestionIndex = action.payload
    },
    clearQuestionHistory: (state) => {
      state.questionHistory = []
      state.currentQuestionIndex = -1
    },
    incrementQuestionAttemptCount: (state) => {
      if (state.currentQuestionIndex >= 0 && state.currentQuestionIndex < state.questionHistory.length) {
        const currentQuestion = state.questionHistory[state.currentQuestionIndex]
        if (currentQuestion) {
          currentQuestion.attemptCount = (currentQuestion.attemptCount || 0) + 1
        }
      }
    },
    setSelectedDifficultyLevel: (state, action: PayloadAction<number>) => {
      state.selectedDifficultyLevel = action.payload
      state.auditLogsCompleteData.payload.input_params_obj.difficulty_level = getDifficultyString(action.payload)
    },
    setFormativeAssessmentFeedbackRating: (state, action: PayloadAction<FeedbackType>) => {
      if (state.formativeAssessmentResult) {
        state.formativeAssessmentResult.feedbackRating = action.payload
      }
    },
    setStartEventId: (state, action: PayloadAction<string | null>) => {
      state.startEventId = action.payload
    },
    setFormativeAssessmentScope: (state, action: PayloadAction<number>) => {
      // Update scope in audit logs payload based on unit selection
      // -1 (All Weeks) = "course", specific week = "week"
      const scope = action.payload === -1 ? "course" : "week"
      state.auditLogsCompleteData.payload.input_params_obj.scope = scope
    },
    // NEW: Populate question history from resume API response for full navigation support
    populateQuestionHistoryFromResume: (
      state,
      action: PayloadAction<{
        questionHistory: ResumeQuestionHistoryItem[]
        currentProblemIndex: number
        testSessionId: string
        testSessionSlug: string
      }>
    ) => {
      const { questionHistory, currentProblemIndex, testSessionId, testSessionSlug } = action.payload

      // Convert BE question_history format to FE questionHistory format
      state.questionHistory = questionHistory.map((item) => {
        const problemTypeKey = getProblemTypeKeyForSlice(item.problem.problemType)
        const stage = PROBLEM_ASSESSMENT_TYPE_MAPPING[problemTypeKey as keyof typeof PROBLEM_ASSESSMENT_TYPE_MAPPING]

        // Build testSessionDetails structure that matches what FE expects
        const testSessionDetails = {
          testSessionId,
          testSessionSlug,
          currentProblem: item.problem,
          totalProblems: questionHistory.length,
          problemsLeft: questionHistory.filter((q) => q.status === null).length,
          isResumed: true,
          questionHistory: questionHistory,
        }

        // Determine problem type - handle both number and string formats
        // Cast to unknown first to allow comparison with both string and number
        const problemType = item.problem.problemType as unknown
        const isTypeMCQ = problemType === 1 || problemType === "mcq" || problemType === "1"
        const isTypeMSQ = problemType === 2 || problemType === "msq" || problemType === "2"
        const isTypeText =
          problemType === 3 ||
          problemType === 4 ||
          problemType === 5 ||
          problemType === "3" ||
          problemType === "4" ||
          problemType === "5" ||
          problemType === "short_answer" ||
          problemType === "long_answer" ||
          problemType === "numeric"

        // Parse submitted answer if it exists
        let selectedMCQOption = null
        let selectedMSQOptions: any[] = []
        let textAnswer = ""
        let correctOptionsForUI: string[] = []

        const submittedAnswer = item.problem.userSubmission?.submittedAnswer || ""

        // Handle [SKIPPED] case - show empty text instead of "[SKIPPED]"
        if (submittedAnswer === "[SKIPPED]" || item.status === "skipped") {
          textAnswer = "" // Don't show "[SKIPPED]" in UI
        } else if (submittedAnswer && (isTypeMCQ || isTypeMSQ)) {
          // MCQ/MSQ: Parse the submitted answer
          // BE sends Python-style list: "['option text']" with single quotes
          // Need to convert to JSON format with double quotes
          try {
            const jsonCompatible = submittedAnswer.replace(/'/g, '"')
            const parsedAnswer = JSON.parse(jsonCompatible) as string[]

            if (Array.isArray(parsedAnswer)) {
              if (isTypeMCQ) {
                // For MCQ, find the option object that matches the submitted option text
                const submittedOptionText: string = parsedAnswer[0] || ""
                const matchingOption = item.problem.options?.find(
                  (opt: any) =>
                    opt.option_text === submittedOptionText ||
                    opt.optionText === submittedOptionText ||
                    opt.id === submittedOptionText
                ) as any
                // Store the matching option with both possible property names
                // Use type assertion since BE sends option_text (snake_case)
                selectedMCQOption = matchingOption
                  ? {
                      id: matchingOption.id,
                      optionText: (matchingOption as any).option_text || matchingOption.optionText,
                    }
                  : { id: submittedOptionText, optionText: submittedOptionText }

                // For correct answers, the submitted option IS the correct option
                if (item.status === "correct") {
                  correctOptionsForUI = [selectedMCQOption.optionText]
                }
              } else if (isTypeMSQ) {
                // For MSQ, map option texts to option objects
                selectedMSQOptions = parsedAnswer.map((optText: string) => {
                  const matchingOption = item.problem.options?.find(
                    (opt: any) => opt.option_text === optText || opt.optionText === optText || opt.id === optText
                  ) as any
                  return matchingOption
                    ? {
                        id: matchingOption.id,
                        optionText: (matchingOption as any).option_text || matchingOption.optionText,
                      }
                    : { id: optText, optionText: optText }
                })

                // For correct answers, the submitted options ARE the correct options
                if (item.status === "correct") {
                  correctOptionsForUI = selectedMSQOptions.map((opt: any) => opt.optionText as string)
                }
              }
            }
          } catch {
            // If parsing fails, log and continue
            console.warn("Failed to parse MCQ/MSQ submitted answer:", submittedAnswer)
          }
        } else if (submittedAnswer && isTypeText) {
          // Text-based questions (short_answer, long_answer, numeric)
          // Use the raw string value directly
          textAnswer = submittedAnswer
        }

        // Determine submitFormativeAssessmentData based on status:
        // - For CORRECT answers: Set full data with correctOptions so UI highlights the correct answer
        // - For WRONG/SKIPPED/NULL answers: Set to NULL so user can RE-ATTEMPT
        let submitFormativeAssessmentData = null

        if (item.status === "correct" && item.problem.userSubmission) {
          // CORRECT: Show as completed with green highlighting
          submitFormativeAssessmentData = {
            status: "pass",
            feedback: item.problem.userSubmission.feedback || "",
            correctOptions: correctOptionsForUI,
            correctExplanation: item.problem.correctExplanation || "",
            // For text-based questions, store the solution
            solution: textAnswer || null,
          }
        }
        // For WRONG, SKIPPED, or NULL status: submitFormativeAssessmentData stays null
        // This allows the user to re-attempt the question

        return {
          testSessionDetails,
          stage,
          problemIndex: item.sequence, // 1-indexed for display
          attemptCount: item.attemptCount || 0,
          submitFormativeAssessmentData,
          selectedMCQOption,
          selectedMSQOptions,
          textAnswer,
          // For wrong/skipped, don't show the feedback animation on load
          isShowingWrongFeedback: false,
          persistedQuickHint: null,
          persistedLongHint: null,
          persistedAnswerStatusType: item.status === "correct" ? "correct" : null,
        }
      })

      // Set current question index to the correct position
      state.currentQuestionIndex = currentProblemIndex
    },
    resetState: () => initialState,
  },
})

export const {
  setStage,
  setCurrentTestSessionDetails,
  setSelectedAssessmentMCQOption,
  setSubmitFormativeAssessmentData,
  setFormativeAssessmentProblemsLeft,
  setFormativeAssessmentTotalProblems,
  setSelectedAssessmentMSQOptions,
  setAuditLogsHeartbeatStarted,
  setAuditLogsCompleteData,
  setFormativeAssessmentResult,
  addToQuestionHistory,
  updateCurrentQuestionFeedback,
  goToPreviousQuestion,
  goToNextQuestion,
  setCurrentQuestionIndex,
  clearQuestionHistory,
  incrementQuestionAttemptCount,
  setSelectedDifficultyLevel,
  setFormativeAssessmentFeedbackRating,
  setStartEventId,
  setFormativeAssessmentScope,
  populateQuestionHistoryFromResume,
  resetState,
} = FormativeAssessmentSlice.actions
export default FormativeAssessmentSlice.reducer
